import React from "react";
import useAnalyses from "../../../api/useAnalyses";
import type { Project } from "../../../api/useProjects";
import type { Source } from "../../../api/useSources";
import { useProjectSource } from "../../../api/useSources";
import { useQuantityIndex } from "../../../api/useQuantities";
import { useCanUserDoOnProject } from "../../canUserDo";
import { isNonEmptyArray } from "diagnostics-lib/array";
import Loading from "@ui/Loading";
import useSelectedAnalyses from "../Analysis/useSelectedAnalyses";
import QuantityLayout from "../Chart/QuantityLayout";
import QuantitySelector, { QuantitySelectorType } from "../QuantitySelector";
import { useProjectPeriod } from "../useProjectPeriod";
import type { SelectedQuantities } from "../useSelectedQuantities";
import useSelectedQuantities from "../useSelectedQuantities";
import MeasuresEvolution from "./MeasuresEvolution";
import { useAllAnalysesMode } from "../useAllAnalysesMode";
import BlankStateMeasures from "./components/BlankStateMeasures";
import { useMeasureChartType } from "./hook/useMeasureChartType";
import { useOutletContext, useSearchParams } from "react-router";
import useActiveAnalysesWithDisplayFilters from "../../../api/useActiveAnalysesWithDisplayFilters";
import useActiveAnalysesStream from "../../../api/useActiveAnalysesStream";

interface PrivateP {
  project: Project;
  source: Source;
  selectedQuantities: SelectedQuantities;
}

const ProjectMeasures = ({ project, selectedQuantities, source }: PrivateP) => {
  const projectId = project.id;
  const [_, setSelectedQuantities] = useSelectedQuantities(project.id);
  const { activeAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);
  const { activeAnalyses: rawActiveAnalyses } = useActiveAnalysesStream(project.id);
  const [_allAnalysesMode, setAllAnalysesMode] = useAllAnalysesMode({ scope: projectId });
  const allAnalysesMode = rawActiveAnalyses.length === 0 || _allAnalysesMode;
  const activeAnalysesForMeasures = allAnalysesMode ? rawActiveAnalyses : activeAnalyses;
  const quantityIndex = useQuantityIndex(projectId);
  const [searchParams] = useSearchParams();
  const allDataParam = searchParams.get("allData");
  const qoiIdParam = searchParams.get("qoiId");
  const { result: analyses, samplingDescription } = useAnalyses(project, source, allAnalysesMode);
  const { selectedAnalyses } = useSelectedAnalyses(projectId, activeAnalysesForMeasures);
  const [period] = useProjectPeriod(
    allAnalysesMode
      ? {
          source,
          project,
          boundaries: "analyses",
        }
      : {
          source,
          project,
          analyses: activeAnalyses,
          boundaries: "active-analyses",
        },
  );
  const canUpdateQuantityValues = useCanUserDoOnProject("PROJECT_WRITE", projectId);

  React.useEffect(() => {
    if (allDataParam === "1") {
      setAllAnalysesMode(true);
    }

    if (!qoiIdParam) return;
    const quantity = quantityIndex[qoiIdParam];
    if (!quantity) return;
    setSelectedQuantities([quantity]);
  }, [allDataParam, qoiIdParam, quantityIndex, setAllAnalysesMode, setSelectedQuantities]);

  const [selectedChartType, setSelectedChartOption] = useMeasureChartType({
    scope: projectId,
    defaultValue: "value",
  });

  const evolution = React.useMemo(
    () => (
      <MeasuresEvolution
        samplingDescription={samplingDescription}
        project={project}
        source={source}
        analyses={analyses}
        activeAnalyses={activeAnalysesForMeasures}
        canUpdateQuantityValues={canUpdateQuantityValues}
        selectedQuantities={selectedQuantities}
        setSelectedChartOption={setSelectedChartOption}
        selectedChartOption={selectedChartType}
        allAnalysesMode={allAnalysesMode}
        setAllAnalysesMode={setAllAnalysesMode}
        rawActiveAnalysesCount={rawActiveAnalyses.length}
      />
    ),
    [
      activeAnalysesForMeasures,
      allAnalysesMode,
      analyses,
      canUpdateQuantityValues,
      project,
      samplingDescription,
      selectedChartType,
      selectedQuantities,
      setAllAnalysesMode,
      setSelectedChartOption,
      source,
      rawActiveAnalyses.length,
    ],
  );

  const quantities = React.useMemo(
    () => (
      <QuantitySelector
        timezone={project.timezone}
        projectId={project.id}
        onlyFirstDisplayed={false}
        selectedQuantities={selectedQuantities}
        setSelectedQuantities={setSelectedQuantities}
        activeAnalyses={activeAnalysesForMeasures}
        selectedAnalyses={selectedAnalyses}
        alertType={QuantitySelectorType.Measures}
        page={QuantitySelectorType.Measures}
        period={period}
        allAnalysesMode={allAnalysesMode}
      />
    ),
    [
      activeAnalysesForMeasures,
      period,
      project.id,
      selectedAnalyses,
      selectedQuantities,
      setSelectedQuantities,
      allAnalysesMode,
      project.timezone,
    ],
  );

  return <QuantityLayout quantities={quantities}>{evolution}</QuantityLayout>;
};

export const ProjectMeasuresNonIdealProxy = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const [selectedQuantities] = useSelectedQuantities(project.id);
  const source = useProjectSource(project);
  if (source === null) return <Loading />; // Will happen temporarily if source is deletable before project gets removed as well or if project is created before source get a chance to be synced

  if (!isNonEmptyArray(selectedQuantities)) return <BlankStateMeasures />;

  return (
    <ProjectMeasures project={project} source={source} selectedQuantities={selectedQuantities} />
  );
};
